import React, { useState, useRef, useCallback } from 'react';
import Button from '../../../shared/components/Button';
import { sha256Hex } from '../../../shared/utils/sha256';
import {
  AGREEMENT_VERSION,
  AGREEMENT_TITLE,
  AGREEMENT_SECTIONS,
  AGREEMENT_DISCLAIMERS_TITLE,
  AGREEMENT_DISCLAIMERS,
  AGREEMENT_PLAIN_TEXT,
} from '../constants/agreementTerms';
import { PRIVACY_VERSION, TERMS_VERSION, MESSAGING_CONSENT_VERSION } from '../constants/policyVersions';
import MessagingConsentText from '../components/MessagingConsentText';

// COPY — customer-facing; PLACEHOLDER wording, route through Frannie/Aria before
// finalizing. Supports the admin's "full names, not initials" enforcement.
const NAME_ENTRY_INSTRUCTION =
  'Please enter your full legal name — both your first name and last name. ' +
  'Do not use initials or abbreviations.';
const NAME_ACK_LABEL =
  'I confirm I am entering my full legal name — first and last, not initials.';

// `submitting` is driven by the parent wizard while it persists consent (invite
// flow). It defaults to false so the self-register flow, which doesn't pass it,
// is unaffected.
const ContributionAgreementScreen = ({ data, onNext, onBack, submitting = false }) => {
  // Prefill from earlier steps when available (the /join flow captures first/last
  // on CreateAccountScreen); empty in the invite flow, where the member enters it here.
  const [firstName, setFirstName] = useState(data?.firstName || '');
  const [lastName, setLastName] = useState(data?.lastName || '');
  const [displayAlias, setDisplayAlias] = useState(data?.displayAlias || '');   // optional (Stage 46)
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [nameAck, setNameAck] = useState(false);  // "entering my full legal name" — forcing function
  const [hasScrolled, setHasScrolled] = useState(false);
  const [errors, setErrors] = useState({});
  const [hashing, setHashing] = useState(false);
  const scrollRef = useRef(null);

  // The member's own legal name is authoritative; the signature must be that name.
  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
  // /join prefills first/last from CreateAccountScreen → show it read-only here
  // ("Signing as …"). The invite flow has no prior entry → editable inputs.
  const nameLocked = Boolean((data?.firstName || '').trim() && (data?.lastName || '').trim());

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || hasScrolled) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
    if (atBottom) setHasScrolled(true);
  }, [hasScrolled]);

  const validateForm = () => {
    const newErrors = {};

    if (!hasScrolled) {
      newErrors.scroll = 'Please scroll through the full agreement before signing';
    }

    // Full names, not initials: require ≥2 chars after trimming. This catches an
    // obvious single-letter initial ("A"); it does NOT prove the name is real or
    // complete — that stays the admin's judgment (visibility), tracked separately.
    if (!firstName.trim()) newErrors.firstName = 'Enter your first name';
    else if (firstName.trim().length < 2) newErrors.firstName = 'Please enter your full first name, not an initial.';
    if (!lastName.trim()) newErrors.lastName = 'Enter your last name';
    else if (lastName.trim().length < 2) newErrors.lastName = 'Please enter your full last name, not an initial.';

    // Required acknowledgment — only in the invite flow, where the name is entered
    // here (the /join flow captured it on an earlier screen and shows it read-only).
    if (!nameLocked && !nameAck) {
      newErrors.nameAck = 'Please confirm you are entering your full legal name.';
    }

    if (!signature.trim()) {
      newErrors.signature = 'Type your full name to sign';
    } else if (firstName.trim() && lastName.trim() && signature.trim().toLowerCase() !== fullName.toLowerCase()) {
      newErrors.signature = `Signature must match your full name: ${fullName}`;
    }

    if (!agreed) {
      newErrors.agreed = 'You must agree to the terms to continue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setHashing(true);
    try {
      // Hash the SAME canonical text the screen rendered (see agreementTerms.js),
      // so the persisted terms_sha256 provably matches what the member saw.
      const termsSha256 = await sha256Hex(AGREEMENT_PLAIN_TEXT);
      // Fire onNext with the full consent payload. The parent owns persistence
      // (and, for the invite flow, the `submitting`/error state after this).
      onNext({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayAlias: displayAlias.trim().slice(0, 40),   // optional; empty -> real name at save
        signature,
        agreed,
        signedAt: new Date().toISOString(),
        termsVersion: AGREEMENT_VERSION,
        termsSha256,
        // The single checkbox covers all documents + messaging consent.
        privacyAccepted: agreed,
        privacyVersion: PRIVACY_VERSION,
        tcAccepted: agreed,
        tcVersion: TERMS_VERSION,
        messagingAccepted: agreed,
        messagingVersion: MESSAGING_CONSENT_VERSION,
        messagingMethod: 'onboarding',
      });
    } catch (err) {
      setErrors((prev) => ({ ...prev, submit: 'Could not prepare your consent record. Please try again.' }));
    } finally {
      setHashing(false);
    }
  };

  const busy = hashing || submitting;

  return (
    <div className="flex flex-col min-h-screen px-4 py-8 bg-neutral-100">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 mb-2">
          Digital Contribution Agreement
        </h1>
        <p className="text-neutral-600">
          Read the full agreement below, then sign to continue
        </p>
      </div>

      <div className="flex-1 space-y-5">
        {/* Scrollable agreement content — GATE */}
        <div>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-72 overflow-y-auto border border-neutral-200 rounded-lg bg-neutral-50"
          >
            <div className="p-5 space-y-4">
              {/* Rendered from the canonical AGREEMENT_* source — the same text
                  that gets hashed into terms_sha256 (see agreementTerms.js). */}
              <h2 className="text-base font-semibold text-neutral-900">{AGREEMENT_TITLE}</h2>

              <div className="space-y-3 text-sm text-neutral-700">
                {AGREEMENT_SECTIONS.map((section) => (
                  <p key={section.heading}>
                    <strong>{section.heading}:</strong> {section.body}
                  </p>
                ))}
              </div>

              {/* Disclaimers inside scroll area */}
              <div className="bg-error-50 border border-error-200 rounded-lg p-4 mt-2">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-error-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="text-sm text-error-800">
                    <p className="font-semibold mb-1">{AGREEMENT_DISCLAIMERS_TITLE}</p>
                    <ul className="space-y-1">
                      {AGREEMENT_DISCLAIMERS.map((line) => (
                        <li key={line}>• {line}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll hint */}
          {!hasScrolled && (
            <p className="mt-2 text-xs text-center text-neutral-400">
              ↓ Scroll down to read the full agreement
            </p>
          )}
          {hasScrolled && (
            <p className="mt-2 text-xs text-center text-success-600">
              ✓ Agreement read
            </p>
          )}
          {errors.scroll && (
            <p className="mt-1 text-sm text-error-600">{errors.scroll}</p>
          )}
        </div>

        {/* Full-name instruction — a prominent amber notice shown where the member
            actually enters their name (invite flow). The /join flow captured the
            name on an earlier screen (read-only here), so this is omitted there.
            A one-time glow on load draws the eye, then settles; skipped for users
            who prefer reduced motion. A required acknowledgment forces engagement. */}
        {!nameLocked && (
          <>
            <style>{`
              @media (prefers-reduced-motion: no-preference) {
                @keyframes nameBannerCue {
                  0%   { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
                  35%  { box-shadow: 0 0 0 6px rgba(217, 119, 6, 0.30); }
                  100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
                }
                .name-banner-cue { animation: nameBannerCue 1.1s ease-out 0.15s 2; }
              }
            `}</style>
            <div className="name-banner-cue my-3 rounded-lg border-2 border-warning-400 bg-warning-50 p-4 shadow-sm">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-warning-600 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-base font-bold text-warning-900">Enter your full legal name</p>
                  <p className="mt-1 text-sm text-warning-800">{NAME_ENTRY_INSTRUCTION}</p>
                  <label className="mt-3 flex items-start space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={nameAck}
                      onChange={(e) => setNameAck(e.target.checked)}
                      disabled={!hasScrolled}
                      className="mt-0.5 text-warning-600 focus:ring-warning-500 disabled:opacity-50"
                    />
                    <span className="text-sm font-medium text-warning-900">{NAME_ACK_LABEL}</span>
                  </label>
                  {errors.nameAck && <p className="mt-1 text-sm text-error-600">{errors.nameAck}</p>}
                </div>
              </div>
            </div>
          </>
        )}

        {/* Legal name. /join already captured it → show read-only so the member
            sees what they're binding under; invite flow enters it fresh. Either
            way it is their name of record and their signature. */}
        {nameLocked ? (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">Signing as</label>
            <p className="text-base font-medium text-neutral-900">{fullName}</p>
            <p className="mt-1 text-xs text-neutral-500">The full legal name you entered when creating your account.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-2">First name *</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={!hasScrolled}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-neutral-100 disabled:text-neutral-400 ${errors.firstName ? 'border-error-500' : 'border-neutral-300'}`}
              />
              {errors.firstName && <p className="mt-1 text-sm text-error-600">{errors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-2">Last name *</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={!hasScrolled}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-neutral-100 disabled:text-neutral-400 ${errors.lastName ? 'border-error-500' : 'border-neutral-300'}`}
              />
              {errors.lastName && <p className="mt-1 text-sm text-error-600">{errors.lastName}</p>}
            </div>
          </div>
        )}

        {/* Optional display name (Stage 46) — how the member appears to peers; real name
            + FHA/admin records are unaffected. Finalized copy (Frannie). */}
        <div>
          <label htmlFor="displayAlias" className="block text-sm font-medium text-neutral-700 mb-2">Display name (optional)</label>
          <input
            type="text"
            id="displayAlias"
            maxLength={40}
            value={displayAlias}
            onChange={(e) => setDisplayAlias(e.target.value)}
            placeholder={fullName || 'Your name'}
            disabled={!hasScrolled}
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-neutral-100 disabled:text-neutral-400"
          />
          <p className="mt-1 text-xs text-neutral-500">
            Optional. This is how your name shows to others in your circle. Your admin and official records always use your real name.
          </p>
        </div>

        {/* Signature — must match the first + last entered above. */}
        <div>
          <label htmlFor="signature" className="block text-sm font-medium text-neutral-700 mb-2">
            Type your full name to sign *
          </label>
          <input
            type="text"
            id="signature"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder={fullName ? `Type: ${fullName}` : 'Type your first and last name'}
            disabled={!hasScrolled}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-neutral-100 disabled:text-neutral-400 ${
              errors.signature ? 'border-error-500' : 'border-neutral-300'
            }`}
          />
          {errors.signature && (
            <p className="mt-1 text-sm text-error-600">{errors.signature}</p>
          )}
          <p className="mt-1 text-xs text-neutral-500">
            Your electronic signature is legally binding
          </p>
        </div>

        {/* Messaging consent (SMS + WhatsApp) — the CANONICAL text bound to
            MESSAGING_CONSENT_VERSION (v2-2026-08). Shown at the moment of agreement;
            the single checkbox below covers it. This exact wording must match the
            re-consent modal + policy pages. Finalized copy (Frannie). */}
        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3">
          <p className="text-xs font-medium text-neutral-700 mb-1">Messages from EsuSol</p>
          <MessagingConsentText />
        </div>

        {/* Checkbox */}
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="agreed"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={!hasScrolled}
            className="mt-1 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
          />
          <div className="flex-1">
            <label htmlFor="agreed" className="text-sm text-neutral-700 cursor-pointer">
              I have read and agree to the Contribution Agreement,{' '}
              <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">Privacy Policy</a>, and{' '}
              <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="text-primary-600 underline hover:text-primary-700">Terms &amp; Conditions</a>,
              I consent to receive the messages described above, and I understand my responsibilities and the risks involved.
            </label>
            {errors.agreed && (
              <p className="mt-1 text-sm text-error-600">{errors.agreed}</p>
            )}
          </div>
        </div>

        {/* Agreement Summary */}
        <div className="bg-primary-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-primary-900 mb-2">Agreement Summary</h3>
          <div className="text-xs text-primary-800 space-y-1">
            <p><strong>Member:</strong> {data?.name || 'Not provided'}</p>
            <p><strong>Group:</strong> {data?.groupName || 'Loading...'}</p>
            <p><strong>Contribution:</strong> {data?.amount ? `$${data.amount}` : 'Not specified'} {data?.frequency || ''}</p>
            <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {errors.submit && (
        <p className="mt-4 text-sm text-error-600 text-center">{errors.submit}</p>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3 mt-8">
        <Button onClick={onBack} variant="secondary" className="flex-1" disabled={busy}>
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          variant="primary"
          className="flex-1"
          disabled={!hasScrolled || (!nameLocked && !nameAck) || busy}
        >
          {busy ? 'Recording…' : 'I Agree and Join →'}
        </Button>
      </div>
    </div>
  );
};

export default ContributionAgreementScreen;
